import React, { useState } from 'react';
import { Platform, View, Text, ScrollView, TouchableOpacity } from 'react-native';
import { AdminColors } from '../constants/adminColors';
import AdminBreadcrumb from '../components/AdminBreadcrumb';
import AdminButton from '../components/AdminButton';
import { HpTab } from './HomepageConfig/types';
import { TABS, ADD_LABELS } from './HomepageConfig/constants';
import { PlusIcon } from './HomepageConfig/icons';
import { styles } from './HomepageConfig/styles';
import BannersTab from './HomepageConfig/BannersTab';
import ChangeLogTab from './HomepageConfig/ChangeLogTab';
import EventsTab from './HomepageConfig/EventsTab';
import TrainingTab from './HomepageConfig/TrainingTab';
import LinksTab from './HomepageConfig/LinksTab';
import { useTheme } from '../../../theme/useTheme';

// ─── Screen ───────────────────────────────────────────────────────────────────
interface HomepageConfigScreenProps {
  onBack: () => void;
}

const HomepageConfigScreen: React.FC<HomepageConfigScreenProps> = ({ onBack }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';

  const [activeTab, setActiveTab] = useState<HpTab>('banners');
  const [showAdd, setShowAdd] = useState(false);

  const renderTab = () => {
    const tabProps = { showAdd, onCloseAdd: () => setShowAdd(false) };
    switch (activeTab) {
      case 'banners':   return <BannersTab {...tabProps} />;
      case 'training':  return <TrainingTab {...tabProps} />;
      case 'events':    return <EventsTab {...tabProps} />;
      case 'links':     return <LinksTab {...tabProps} />;
      case 'changelog': return <ChangeLogTab {...tabProps} />;
    }
  };

  return (
    <ScrollView
      style={[styles.container, isDark && { backgroundColor: colors.background.page }]}
      contentContainerStyle={styles.content}
    >
      <AdminBreadcrumb
        items={[
          { label: 'Configurations', onPress: onBack },
          { label: 'Homepage Configuration' },
        ]}
      />
      <View style={styles.pageHeader}>
        <View>
          <Text style={[styles.pageTitle, isDark && { color: colors.text.primary }]}>
            Homepage Configuration
          </Text>
          <Text style={[styles.pageSubtitle, isDark && { color: colors.text.secondary }]}>
            Global — content visible to all Navigator users
          </Text>
        </View>
      </View>

      {/* Tabs */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={[styles.tabBar, isDark && { borderBottomColor: colors.border.divider }]}
        contentContainerStyle={styles.tabBarContent}
      >
        {TABS.map(tab => (
          <TouchableOpacity
            key={tab.key}
            style={[
              styles.tab,
              activeTab === tab.key && styles.tabActive,
              isDark && activeTab === tab.key && { borderBottomColor: colors.brand.primary },
            ]}
            onPress={() => { setActiveTab(tab.key); setShowAdd(false); }}
            activeOpacity={0.7}
          >
            <Text
              style={[
                styles.tabLabel,
                activeTab === tab.key && styles.tabLabelActive,
                isDark && { color: activeTab === tab.key ? colors.brand.primary : colors.text.secondary },
              ]}
            >
              {tab.label}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      {/* Add button — transparent background, above surface */}
      <View style={styles.addButtonRow}>
        <AdminButton label={ADD_LABELS[activeTab]} variant="primary" icon={<PlusIcon />} onPress={() => setShowAdd(true)} />
      </View>

      {/* Tab content within a surface card */}
      <View
        style={[
          styles.surface,
          isDark && {
            backgroundColor: colors.background.surface,
            shadowColor: 'transparent',
          },
        ]}
      >
        {renderTab()}
      </View>
    </ScrollView>
  );
};

export default HomepageConfigScreen;
